/* eslint-disable multiline-ternary */
import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useToast,
  Heading
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useRole } from '../../../../../providers/RoleProvider'
import {
  getQueueService,
  getSecretaryService
} from '../../../../../services/Services'
import { type QueueEntryType } from '../../../../../model/queue/QueueEntry'
import messages from './messages'

export default function TodayQueue() {
  const { doctorId, loading: roleLoading } = useRole()
  const toast = useToast()
  const { formatMessage } = useIntl()

  const [hasSecretary, setHasSecretary] = useState<boolean | null>(null)
  const [entries, setEntries] = useState<QueueEntryType[]>([])
  const [loading, setLoading] = useState(true)
  const [markingDoneId, setMarkingDoneId] = useState<string | null>(null)

  // Check if secretary exists
  useEffect(() => {
    if (roleLoading || !doctorId) return

    getSecretaryService()
      .getSecretaryByDoctorId(doctorId)
      .then((secretary) => {
        setHasSecretary(!!secretary)
      })
      .catch(() => {
        setHasSecretary(false)
      })
  }, [doctorId, roleLoading])

  // Subscribe to today's queue
  useEffect(() => {
    if (roleLoading || !doctorId) return

    setLoading(true)
    const unsubscribe = getQueueService().getTodayQueue(
      doctorId,
      (updatedEntries) => {
        setEntries(updatedEntries)
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [doctorId, roleLoading])

  const handleMarkDone = async (entryId: string) => {
    if (markingDoneId !== null) return
    setMarkingDoneId(entryId)
    try {
      await getQueueService().updateStatus(entryId, 'done')
    } catch {
      toast({
        title: formatMessage(messages.failedToMarkDone),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setMarkingDoneId(null)
    }
  }

  if (roleLoading || loading) {
    return (
      <Flex
        flex={1}
        flexDir="column"
        gap={4}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        p={5}
      >
        <Heading size="sm">
          <FormattedMessage {...messages.todaysQueue} />
        </Heading>
        <Box flex={1}>
          <Flex justifyContent="center">
            <Spinner />
          </Flex>
        </Box>
      </Flex>
    )
  }

  if (hasSecretary === false) {
    return (
      <Flex
        flex={1}
        flexDir="column"
        gap={4}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        p={5}
      >
        <Heading size="sm">
          <FormattedMessage {...messages.todaysQueue} />
        </Heading>
        <Box flex={1}>
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Text color="gray.500">
              <FormattedMessage {...messages.addASecretary} /> {'→ '}
              <Link
                to="/settings?section=secretary"
                style={{
                  color: 'var(--chakra-colors-blue-500)',
                  textDecoration: 'underline'
                }}
              >
                <FormattedMessage {...messages.settings} />
              </Link>
            </Text>
          </Flex>
        </Box>
      </Flex>
    )
  }

  const waitingCount = entries.filter((e) => e.status === 'waiting').length

  return (
    <Flex
      flex={1}
      flexDir="column"
      gap={4}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      p={5}
    >
      <Flex alignItems="center" gap={2}>
        <Heading size="sm">
          <FormattedMessage {...messages.todaysQueue} />
        </Heading>
        {waitingCount > 0 && (
          <Badge colorScheme="green" borderRadius="full" px={2}>
            {waitingCount} {formatMessage(messages.waiting).toLowerCase()}
          </Badge>
        )}
      </Flex>
      <Box flex={1}>
        {entries.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Text color="gray.500">
              <FormattedMessage {...messages.noPatientsInQueueToday} />
            </Text>
          </Flex>
        ) : (
          <Flex flexDir="column" gap={2}>
            {entries.map((entry, index) => {
              const isDone = entry.status === 'done'
              const addedAtDate = new Date(entry.addedAt)
              const timeString = addedAtDate.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })

              return (
                <Flex
                  key={entry.id}
                  data-testid={`queue-entry-${entry.id}`}
                  p={2}
                  borderRadius={8}
                  alignItems="center"
                  justifyContent="space-between"
                  opacity={isDone ? 0.5 : 1}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Flex gap={3} alignItems="center">
                    <Text
                      fontWeight="bold"
                      color="gray.500"
                      minW="24px"
                      textAlign="center"
                    >
                      {index + 1}
                    </Text>
                    <Link to={`/patients/${entry.patientId}`}>
                      <Text
                        fontWeight="medium"
                        _hover={{
                          textDecoration: 'underline',
                          color: 'blue.500'
                        }}
                        textDecoration={isDone ? 'line-through' : 'none'}
                      >
                        {entry.patientName}
                      </Text>
                    </Link>
                    <Text color="gray.400" fontSize="xs">
                      {timeString}
                    </Text>
                  </Flex>
                  <Flex gap={2} alignItems="center">
                    <Badge colorScheme={isDone ? 'gray' : 'green'}>
                      {isDone
                        ? formatMessage(messages.done)
                        : formatMessage(messages.waiting)}
                    </Badge>
                    {!isDone && (
                      <Button
                        size="xs"
                        colorScheme="gray"
                        variant="outline"
                        onClick={async () => {
                          await handleMarkDone(entry.id)
                        }}
                        isDisabled={markingDoneId === entry.id}
                        isLoading={markingDoneId === entry.id}
                      >
                        <FormattedMessage {...messages.markDone} />
                      </Button>
                    )}
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        )}
      </Box>
    </Flex>
  )
}
